import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBell, 
  FaTimes, 
  FaCalendarCheck, 
  FaCalendarDay,
  FaInfoCircle,
  FaCheckCircle
} from 'react-icons/fa';

const NotificationItem = ({ notification, onDismiss, onActionClick }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'pickup-today':
        return <FaCalendarDay className="text-red-600 text-xl" />;
      case 'return-today':
        return <FaCalendarCheck className="text-red-600 text-xl" />;
      case 'pickup-tomorrow':
        return <FaCalendarDay className="text-amber-500 text-xl" />;
      case 'return-tomorrow': 
        return <FaCalendarCheck className="text-amber-500 text-xl" />;
      default:
        return <FaInfoCircle className="text-blue-500 text-xl" />;
    }
  };

  const getBgColor = () => {
    switch (notification.type) {
      case 'pickup-today':
      case 'return-today':
        return 'bg-red-50 border-red-200';
      case 'pickup-tomorrow':
      case 'return-tomorrow':
        return 'bg-amber-50 border-amber-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getTimeString = () => {
    if (!notification.time) return '';
    
    const date = new Date(notification.time);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getActionText = () => {
    switch (notification.type) {
      case 'pickup-today':
        return 'Mark as Confirmed';
      case 'return-today':
        return 'Mark as Completed';
      default:
        return null;
    }
  };

  const actionText = getActionText();

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      exit={{ opacity: 0, y: -20, height: 0 }}
      className={`flex flex-col p-4 mb-2 rounded-lg border shadow-sm ${getBgColor()}`}
    >
      <div className="flex items-start">
        <div className="mr-3 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1">
          <div className="font-medium">{notification.title}</div>
          <div className="text-sm text-gray-600">{notification.message}</div>
          {notification.time && (
            <div className="text-xs font-medium mt-1">
              Time: {getTimeString()}
            </div>
          )}
          {notification.customer && (
            <div className="text-xs font-medium mt-1">
              Customer: {notification.customer}
            </div>
          )}
        </div>
        <button 
          onClick={() => onDismiss(notification.id)} 
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimes />
        </button>
      </div>

      {/* Action Button */}
      {actionText && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onActionClick(notification)}
            className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-1 px-3 rounded transition-colors"
          >
            <FaCheckCircle className="text-xs" />
            {actionText}
          </button>
        </div>
      )}
    </motion.div>
  );
};

const NotificationSystem = ({ rentals, onRentalUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // Generate notifications based on rental status and dates
  useEffect(() => {
    if (!rentals || rentals.length === 0) return;

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Set to start of day
    
    const tomorrowDate = new Date(currentDate);
    tomorrowDate.setDate(currentDate.getDate() + 1);
    
    let newNotifications = [];
    
    rentals.forEach(rental => {
      const pickupDate = new Date(rental.pickupDateTime);
      pickupDate.setHours(0, 0, 0, 0);
      
      const returnDate = new Date(rental.returnDateTime);
      returnDate.setHours(0, 0, 0, 0);
      
      const customerName = `${rental.customer.firstName} ${rental.customer.lastName}`;
      const pickupTime = new Date(rental.pickupDateTime);
      const returnTime = new Date(rental.returnDateTime);
      
      // Handle pickups
      if (pickupDate.getTime() === currentDate.getTime() && rental.status === 'pending') {
        // Add urgent notification for today's pickup
        newNotifications.push({
          id: `pickup-today-${rental._id}`,
          type: 'pickup-today',
          title: '🔴 URGENT: Car Pickup Today',
          message: `${rental.carName} is scheduled for pickup today at ${rental.pickupLocation}`,
          time: pickupTime,
          customer: customerName,
          rentalId: rental._id,
          targetStatus: 'confirmed'
        });
      }
      else if (pickupDate.getTime() === tomorrowDate.getTime() && rental.status === 'pending') {
        // Add notification for tomorrow's pickup
        newNotifications.push({
          id: `pickup-tomorrow-${rental._id}`,
          type: 'pickup-tomorrow',
          title: '⚠️ Car Pickup Tomorrow',
          message: `${rental.carName} is scheduled for pickup tomorrow at ${rental.pickupLocation}`,
          time: pickupTime,
          customer: customerName,
          rentalId: rental._id
        });
      }
      
      // Handle returns
      if (returnDate.getTime() === currentDate.getTime() && rental.status === 'confirmed') {
        // Add urgent notification for today's return
        newNotifications.push({
          id: `return-today-${rental._id}`,
          type: 'return-today',
          title: '🔴 URGENT: Car Return Today',
          message: `${rental.carName} is scheduled for return today at ${rental.returnLocation}`,
          time: returnTime,
          customer: customerName,
          rentalId: rental._id,
          targetStatus: 'completed'
        });
      }
      else if (returnDate.getTime() === tomorrowDate.getTime() && rental.status === 'confirmed') {
        // Add notification for tomorrow's return
        newNotifications.push({
          id: `return-tomorrow-${rental._id}`,
          type: 'return-tomorrow',
          title: '⚠️ Car Return Tomorrow',
          message: `${rental.carName} is scheduled for return tomorrow at ${rental.returnLocation}`,
          time: returnTime,
          customer: customerName,
          rentalId: rental._id
        });
      }
    });
    
    // Update notifications
    setNotifications(newNotifications);
    setNotificationCount(newNotifications.length);
  }, [rentals]);

  const dismissNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
    setNotificationCount(prev => Math.max(0, prev - 1));
  };

  const handleActionClick = (notification) => {
    if (notification.targetStatus && notification.rentalId) {
      onRentalUpdate(notification.rentalId, notification.targetStatus);
      // Remove this notification after action is taken
      dismissNotification(notification.id);
    }
  };

  const toggleNotifications = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button 
        onClick={toggleNotifications}
        className="relative p-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <FaBell size={20} />
        {notificationCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {notificationCount}
          </span>
        )}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden"
          >
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0">
              <h3 className="font-medium text-gray-800">Notifications</h3>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto p-2">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No notifications
                </div>
              ) : (
                <AnimatePresence>
                  {notifications.map(notification => (
                    <NotificationItem 
                      key={notification.id} 
                      notification={notification} 
                      onDismiss={dismissNotification}
                      onActionClick={handleActionClick}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationSystem;